import React, { useMemo } from 'react';
import { MobileDownloadIcon } from 'src/components/SuitesTable/components/mobileDownloadIcon';
import CategoryPill from 'src/components/CategoryPill/CategoryPill';
import { variables, Colors } from 'src/Tenants';
import moment from 'moment/moment';
import { NextAppointment } from 'src/components/SuitesTable/cells/NextAppointment';
import { serviceRequestTableLocalTime } from 'src/helpers/validationHelper';
import { sortByNumberOrString } from '../Table/sorting';
import { ITableColumn } from '../Table/types';

const WarrantyDateCell = ({ data }: any) =>
  data.warrantyStartDate ? (
    <div style={{ textAlign: 'center' }}>
      {serviceRequestTableLocalTime(data.warrantyStartDate) === '01/01/0001'
        ? ''
        : serviceRequestTableLocalTime(data.warrantyStartDate)}
    </div>
  ) : null;

const SuiteNumberCell = ({ data, onSuiteClick }: any) => {
  return (
    <div
      className="suites-table-number-cell"
      onClick={() => onSuiteClick(data.id, 'view')}
    >
      <span title={data?.number}>{data && data.number}</span>
      {data?.isCommonArea && (
        <CategoryPill
          text="Common Area"
          size="small"
          color={Colors.toggleAndButtonColor}
        />
      )}
    </div>
  );
};

const BuildingCell = ({ data }: any) => {
  const buildingName = useMemo(() => {
    return data.building || '';
  }, [data.building]);

  return (
    <div title={buildingName}>
      {buildingName}
    </div>
  );
};

const ServiceRequestsCell = ({ data }: any) => (
  <div
    title={`Walk-through: ${data.walkthroughServiceRequestsCount}, Other: ${data.normalServiceRequestsCount}`}
    style={{ textAlign: 'center' }}
  >
    {data.serviceRequests}
  </div>
);

const HomeOwnerCell = ({ data }: any) =>
  data.homeOwners && data.homeOwners.id ? (
    <div style={{ textAlign: 'center' }}>
      {data.homeOwners.firstname + ' ' + data.homeOwners.lastname}
    </div>
  ) : null;

const createSuitesTableColumns = (onSuiteClick: any, onToggleMobileSync: any): ITableColumn[] => [
  {
    label: '',
    accessor: 'mobileSyncAllowed',
    xsHide: true,
    sortable: true,
    width: '6rem',
    minWidth: '6rem',
    cell: (data: any) => (
      <MobileDownloadIcon property={data} onClick={()=>onToggleMobileSync(data.id, !data.mobileSyncAllowed)} />
    ),
  },
  {
    label: variables.Suite,
    sortable: true,
    accessor: 'number',
    minWidth: '18rem',
    width: '20rem',
    cell: (data: any) => (
      <SuiteNumberCell data={data} onSuiteClick={onSuiteClick} />
    ),
  },
  {
    center: true,
    sortable: true,
    label: 'Building',
    accessor: 'building',
    minWidth: '12rem',
    sortingAlgorithm: sortByNumberOrString,
    cell: (data: any) => <BuildingCell data={data} />,
  },
  {
    center: true,
    xsHide: true,
    sortable: true,
    label: variables.serviceRequest + 's',
    accessor: 'serviceRequests',
    minWidth: '12rem',
    cell: (data: any) => <ServiceRequestsCell data={data} />,
  },
  {
    center: true,
    label: variables.homeOwner,
    accessor: 'sortableHomeownerName',
    sortable: true,
    minWidth: '16rem',
    cell: (data: any) => <HomeOwnerCell data={data} />,
  },
  {
    center: true,
    xsHide: true,
    sortable: true,
    label: 'Status',
    accessor: 'status',
    minWidth: '10rem',
  },
  {
    center: true,
    xsHide: true,
    sortable: true,
    accessor: 'warrantyStartDate',
    label: 'Warranty Start Date',
    minWidth: '14rem',
    cell: (data: any) => <WarrantyDateCell data={data} />,
  },
  {
    center: false,
    xsHide: true,
    label: 'Appointment',
    sortable: true,
    accessor: 'nextAppointment',
    minWidth: '16rem',
    sortingAlgorithm: (a, b) => {
      if (a === null) return 1;
      if (b === null) return -1;

      try {
        const parsedJsonA: any = JSON.parse(a);
        const parsedJsonB: any = JSON.parse(b);
        return moment(parsedJsonA?.timeStamp).diff(parsedJsonB?.timeStamp);
      } catch {
        return 0;
      }
    },
    cell: (data: any) => (
      <NextAppointment nextAppointment={data.nextAppointment} />
    ),
  },
];

export { createSuitesTableColumns };
